import {
  isSameMinute as isSameMinuteFromDateFns,
  isToday as isTodayFromDateFns,
  isValid,
} from 'date-fns';
import { date as formatDate } from '@frameio/i18n';
import { formatDateDistanceToNow, getFormattedDate } from '@frameio/utils';
import TEXT from './generated/TEXT/GetScreenReaderTextForTimestamp';

TEXT.DEFINE({
  /** Formats relative timestamp for today */
  formattedTimeToday: (relativeTime?: string, time?: string): string => {
    return `${relativeTime} ago at ${time}`;
  },
  /** Formats relative timestamp for before today */
  formattedTimeBeforeToday: ({
    relativeTime,
    time,
    date,
  }: {
    relativeTime?: string;
    time?: string;
    date: Date;
  }): string => {
    return `${relativeTime} ago at ${time} on ${formatDate(date, 'medium')}`;
  },
});

/**
 * @description `getScreenReaderTextForTimestamp` - generate formatted timestamp
 * text for screen reader.
 * e.g. "2 days ago at 2:40 PM on Sep 12, 2024"
 *
 * @param {string} timestamp - the timestamp to be formatted for the screen
 * reader
 *
 * @returns {null | string}
 **/
export const getScreenReaderTextForTimestamp = ({
  timestamp,
}: {
  timestamp: string;
}): string => {
  const date = new Date(timestamp);

  if (!isValid(date)) return '';

  const isWithinLastMinute = isSameMinuteFromDateFns(Date.now(), date);

  /** Pass the `truncate` option to get back "just now" if the timestamp was less
   * than a minute ago (without it, we'd get the difference in seconds which is
   * _not_ what should be read by the screen reader) */
  if (isWithinLastMinute) {
    return (
      formatDateDistanceToNow({
        date,
        truncate: true,
      }) ?? ''
    );
  }

  const { formattedTime } = getFormattedDate({
    date,
    returnDateParts: true,
  });

  const relativeTime = formatDateDistanceToNow({ date }) ?? '';
  const isToday = isTodayFromDateFns(date);

  if (isToday) {
    // e.g. "10 minutes ago at 2:40 PM"
    return TEXT.formattedTimeToday(relativeTime, formattedTime);
  }

  // e.g. "2 days ago at 2:40 PM on Nov 12, 2024"
  return TEXT.formattedTimeBeforeToday({
    relativeTime,
    time: formattedTime,
    date,
  });
};
